import { segment } from '@actions/analytics';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import { getMeLatestUserSubscriptionsForGoal } from '@api/selectors/subscription';
import { LOGIN_DRAWER } from '@constants/toggleables';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { useToggleable } from '@stores/toggleables';
import { useEffect, useMemo } from 'react';
import { activateFreeTrial } from './api';
import useFreeTrialStore, { setActivateButtonClicked } from './freeTrialStore';

const useFreeTrial = (goalUID) => {
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const isClicked = useFreeTrialStore((state) => state.buttonClicked);
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const subscriptions = useMyInfo(getMeLatestUserSubscriptionsForGoal(goalUID));
  const { refetchMyInfo } = useMyInfo();
  const goalEventsProps = useGoalEventProps({ goalUID });
  const freeTrialSubscription = useMemo(
    () =>
      subscriptions.find(
        (subscription) => subscription.subscription.type === 2
      ),
    [subscriptions]
  );

  const hadFreeTrialSubscription = freeTrialSubscription?.timeRemaining <= 0;
  const hasFreeTrialSubscription = freeTrialSubscription?.timeRemaining > 0;

  const hadSubscription = subscriptions.find(
    (subscription) => subscription.timeRemaining === 0
  );

  const handleAnalytics = () => {
    segment.track('Free Trial - Free Trial Activated', {
      ...goalEventsProps,
      last_primary_source: 'Goal Home',
      last_primary_source_section: 'Free Trial Section'
    });
  };

  useEffect(() => {
    if (isLoggedIn && isClicked && subscriptions.length === 0) {
      // hit api
      activateFreeTrial(goalUID).then((res) => {
        if (res?.error?.errorCode) return;
        handleAnalytics();
        refetchMyInfo();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const onClick = () => {
    setActivateButtonClicked(true);
    if (!isLoggedIn) {
      openLoginDrawer();
    } else {
      // hit api
      activateFreeTrial(goalUID).then((res) => {
        if (res?.error?.errorCode) return;
        handleAnalytics();
        refetchMyInfo();
      });
    }
  };

  return {
    onClick,
    hadFreeTrialSubscription,
    hasFreeTrialSubscription,
    hadSubscription,
    freeTrialSubscription: freeTrialSubscription || {}
  };
};

export default useFreeTrial;
